import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NotificationType, SignalType } from 'angular-to-phaser';
import { Observable, catchError, combineLatest, merge, shareReplay, switchMap, tap } from 'rxjs';
import { Channel, Friendship, Message, MessageResponse, Notification, NotificationSignal, Signal } from '../dto';
import { ApplicationService } from './application.service';
import { BaseMessagingService } from './base-messaging.service';
import { NotificationService } from './notification.service';
import { FriendshipToast, MessageToast, SignalService } from './signal.service';

@Injectable({
    providedIn: 'root'
})
export class InboxService extends BaseMessagingService {

    private readonly allFriendAndReqsState: WritableSignal<Friendship[]> = signal<Friendship[]>([]);
    public readonly allFriendAndReqs$: Observable<Friendship[]> = toObservable(this.allFriendAndReqsState);

    public readonly friendsListSignal = computed(() => this.allFriendAndReqsState().filter(fd => fd.Accepted));
    public readonly friendsList$: Observable<Friendship[]> = toObservable(this.friendsListSignal);

    // Reads NotificationService's own raw signal directly (rather than combineLatest over two
    // Observable bridges) now that both sources are signals - tags each pending request with its
    // matching notification as a side effect, same as the map() this replaced.
    public readonly friendRequestsSignal = computed(() => {
        const requests = this.allFriendAndReqsState().filter(fd => !fd.Accepted);
        const notifications = this.notificationService.privateNotificationsSignal().filter(n => n.NotificationType == NotificationType.FriendshipRequest);
        requests.forEach(frq => frq.targetingNotification = notifications.find(note => frq.equals(<Friendship>note.Entity)));
        return requests;
    });
    public readonly friendRequests$: Observable<Friendship[]> = toObservable(this.friendRequestsSignal);

    private readonly onlineFriendsState: WritableSignal<Friendship[]> = signal<Friendship[]>([]);
    public readonly onlineFriendsSignal = this.onlineFriendsState.asReadonly();

    public readonly offlineFriendsSignal = computed(() => {
        const online = this.onlineFriendsState();
        return this.friendsListSignal().filter(all =>
            !online.find(on => on.RecipientUsername === all.RecipientUsername && on.SenderUsername === all.SenderUsername));
    });


    constructor(notificationService: NotificationService, private signalService: SignalService, coreAppService: ApplicationService, http: HttpClient) {
        super(coreAppService, http, notificationService);
        this.coreAppService.notificationService.latestNotificationSignal.subscribe(this.receiveNotification.bind(this));

        var authSubscription = coreAppService.userService.activeUser$.subscribe(user => {
            if (user && user.Username && this.authService.isAuthenticated()) {
                this.coreAppService.notificationService.initializeConnection();
                combineLatest([this.getPrivateMessagesPage(true), this.retrieveChannels('private')]).subscribe(() => {
                    authSubscription.unsubscribe(); //one and done
                });
            }
        });

        if (this.authService.isAuthenticated()) {
            this.initializeActiveUserFriends();
        } else {
            this.coreAppService.userService.activeUser$.subscribe(a => {
                if (!this.allFriendAndReqsState().length && this.authService.isAuthenticated()) {
                    this.initializeActiveUserFriends();
                }
            });
        }

        this.notificationService.persistentPrivateNotifications.pipe(
            switchMap(notifications => {
                let channelGroupedNotifications = notifications
                    .filter(n => n.NotificationType == NotificationType.PrivateMessage)
                    .map(n => {
                        (<Message>n.Entity).targetingNotification = n;
                        return <Message>n.Entity;
                    })
                    .reduce(
                        (entryMap, e) => entryMap.set(e.ChannelId, [...entryMap.get(e.ChannelId) || [], e]),
                        new Map<number, Message[]>()
                    );
                let keyset = [...channelGroupedNotifications.keys()];
                let inserts$ = keyset.map(k => this.insertMessagesToChannel(channelGroupedNotifications.get(k), true))
                return merge(...inserts$);
            }),
            shareReplay(1)).subscribe();
    }

    public findPrivateChat(withUsername: string): Channel | null {
        const allPrivateChannels = Array.from(this.allChannelsMapSignal().values()).filter(ch => ch().Usernames.length === 2).map(ch => ch());
        const privateMessage = allPrivateChannels.find(ch => ch.Usernames.includes(withUsername));
        return privateMessage;
    }

    /**
       * Retrieves a page of messages from the server and adds them to their viewable parent channels.
       * If fetchNewest is false, 100 messages will be fetched backwards in time from the oldest currently loaded message, otherwise only fetches
       * newer messages that have occurred after initial page load.
       */
    public getPrivateMessagesPage(fetchNewest: boolean): Observable<MessageResponse> {
        let lastMessageId = null;
        return this.retrieveMessages(fetchNewest ? null : lastMessageId, null, 'private');
    }

    private initializeActiveUserFriends(): void {
        this.retrieveFriends().subscribe(friends => this.allFriendAndReqsState.set(friends ?? []));
        this.friendsList$.subscribe(fds => {
            //update friends list periodically
            this.retrieveOnlineFriends();
        });
        setInterval(this.retrieveOnlineFriends.bind(this), 60 * 1000);

    }

    public retrieveFriends(): Observable<Friendship[]> {
        return this.coreAppService.unifiedRepo.retrieveWithNoId(true, true, Friendship);
    }

    private retrieveOnlineFriends(): void {
        combineLatest([this.friendsList$,
        this.coreAppService.unifiedRepo.friendshipRepository.retrieveOnlineFriends()
        ]).subscribe(
            friendsAndOnlineFriendUsernames => {
                const allFriends = friendsAndOnlineFriendUsernames[0];
                if (allFriends) {
                    const onlines = allFriends.filter(x => friendsAndOnlineFriendUsernames[1].find(ofu => ofu === x.SenderUsername || ofu === x.RecipientUsername));
                    this.onlineFriendsState.set(onlines);
                    // offlineFriends$ derives from this + friendsList$ via offlineFriendsSignal - no separate push needed.
                }
            });
    }

    public addFriend(friendUsername: string): Observable<Friendship> {
        this.notificationService.markNotificationsAsRead(this.findFriendshipNotification(friendUsername)).subscribe();
        return this.coreAppService.unifiedRepo.friendshipRepository.addFriend(friendUsername)
            .pipe(
                switchMap(friend => this.coreAppService.jsonMapper.hydrate(friend, Friendship)
                    .pipe(
                        tap(friendshipHydrated => this.coreAppService.jsonMapper.cache(friendshipHydrated, Friendship))
                    )
                ),
                tap(friendship => {
                    const activeUsername = this.coreAppService.userService.activeUser$.getValue().Username;
                    this.addFriendWithoutDupes(activeUsername, friendUsername, friendship);
                }),
            );
    }

    private addFriendWithoutDupes(activeUsername: string, friendUsername: string, friendship: Friendship) {
        let friends = this.allFriendAndReqsState() ?? [];
        friends = friends.filter(frq => frq.FriendUsername.localeCompare(friendUsername) !== 0);//prevent dupes in case the Notification beat us to the punch
        friends.push(friendship);
        this.allFriendAndReqsState.set(friends);
    }

    public removeFriend(friendUsername: string): Observable<void> {
        this.notificationService.removeNotification(this.findFriendshipNotification(friendUsername));
        return this.coreAppService.unifiedRepo.friendshipRepository.removeFriend(friendUsername)
            .pipe(
                tap(() => {
                    const allFriendships = this.allFriendAndReqsState();
                    let removedFriend = allFriendships.find(f => f.FriendUsername.localeCompare(friendUsername) === 0)
                    this.coreAppService.unifiedRepo.uncacheItems(Friendship, removedFriend);
                    if (allFriendships !== null && allFriendships.length > 0) {
                        this.allFriendAndReqsState.set(allFriendships.filter(f => f !== removedFriend));
                    }
                }),
                catchError(this.handleError),
            );
    }

    private findFriendshipNotification(friendUsername: string) {
        var friend = this.allFriendAndReqsState().find(f => f.FriendUsername == friendUsername);
        return friend?.targetingNotification;
    }

    private receiveNotification(value: Signal) {
        if (value?.SignalType !== SignalType.Notification) return;

        let notification = (<NotificationSignal>value).Notification;
        if (notification.NotificationType != NotificationType.FriendshipRequest && notification.NotificationType != NotificationType.PrivateMessage) return;

        if (notification.NotificationType == NotificationType.FriendshipRequest) {

            // hydrate (not map) so the pushed Friendship's Profile (avatar, etc.) is fetched from the server -
            // the raw SignalR payload doesn't carry a nested Profile the way a GET /friends response does.
            this.coreAppService.jsonMapper.hydrate(<Friendship>notification.Entity, Friendship).subscribe(notificationFriendship => {
                const activeUser = this.coreAppService.userService.activeUser$.getValue();
                const activeUsername = activeUser.Username;

                const allFriends = this.allFriendAndReqsState();

                const extantRequestIndex = allFriends.findIndex(fr => fr.equals(notificationFriendship))

                if (extantRequestIndex >= 0) {
                    if (!notificationFriendship.Accepted) {
                        // A signal's default equality check is by reference, so writing back the
                        // same (mutated) array a splice/push would produce is silently treated as
                        // "unchanged" and never notifies friendsList$/friendRequests$ - build a new
                        // array instead, same as addFriendWithoutDupes/removeFriend already do.
                        this.allFriendAndReqsState.set(allFriends.filter((_, i) => i !== extantRequestIndex)); //remove rejected requests
                    }
                    else //overwrite the old request with the new friendship
                        this.addFriendWithoutDupes(activeUsername, notificationFriendship.FriendUsername, notificationFriendship);
                } else { //not in the list yet, meaning they just sent to use
                    this.allFriendAndReqsState.set([...allFriends, notificationFriendship]);
                }
                const toast = new FriendshipToast(value.AlertText, notificationFriendship, 7000, activeUser);

                if (toast.visibleDuration > 0) {
                    this.signalService.addToast(toast);
                }
            });
        } else if (notification.NotificationType == NotificationType.PrivateMessage) {
            let newMessage = <Message>notification.Entity;

            this.insertMessagesToChannel([newMessage]).subscribe((channelAndMessages) => {
                if (!newMessage.IsMyMessage) {
                    this.coreAppService.notificationService.addToast(
                        new MessageToast(newMessage.DisplayText,
                            newMessage.SenderUsername,
                            channelAndMessages.channel,
                            3500));
                }
            });
        }
        this.notificationService.addNotification(notification);
    }
}
